/**
 * Coupon code field — shared by the cart drawer and the cart page.
 *
 * These two elements used to live in cart-drawer.js, which only loads for
 * cart_type = "drawer". "Enable coupon code" is a global cart setting, so the panel now
 * renders on the cart page too and its behaviour had to move somewhere both can load.
 *
 * Everything is scoped to the element rather than the document: with cart_type "drawer"
 * a shopper on /cart has BOTH panels in the page at once, and the old
 * `document.querySelector('cart-coupon-form')` would drive whichever came first.
 */

if (!customElements.get('promo-code-wrap')) {
  customElements.define(
    'promo-code-wrap',
    class PromoCodeSection extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          const toggle = event.target.closest('.cart-coupon-main');
          if (!toggle || !this.contains(toggle)) return;
          const panel = this.querySelector('.promo-code-wrap');
          if (!panel) return;
          const open = panel.classList.toggle('open');
          // The disclosure has to report its own state, or a screen reader announces the
          // same "collapsed" button whether the panel is open or shut.
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) this.querySelector('.promo-input.couponCode')?.focus();
        });
      }
    }
  );
}

if (!customElements.get('cart-coupon-form')) {
  customElements.define(
    'cart-coupon-form',
    class CartCouponCodeButton extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.input = this.querySelector('.promo-input.couponCode');
        this.button = this.querySelector('.btn-promo');
        this.spinner = this.querySelector('.loading-overlay__spinner');
        this.feedback = this.querySelector('[data-coupon-feedback]');

        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          const code = (this.input?.value || '').trim();
          if (!code) {
            this.input?.focus();
            return;
          }
          this.submitDiscount(code, code);
        });

        // Remove buttons on the applied-code chips.
        this.addEventListener('click', (event) => {
          const remove = event.target.closest('[data-remove-discount]');
          if (!remove) return;
          event.preventDefault();
          this.submitDiscount('', remove.getAttribute('data-remove-discount'));
        });

        // Typing again clears the previous failure, so a stale error can't sit under a code
        // the shopper has already corrected.
        this.input?.addEventListener('input', () => {
          if (!this.feedback) return;
          this.feedback.textContent = '';
          this.feedback.classList.add('hidden');
        });
      }

      /** Disable the field and show the spinner, so the request is visibly in progress. */
      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
        this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        if (this.button) this.button.disabled = isLoading;
        if (this.input) this.input.disabled = isLoading;
        this.querySelectorAll('[data-remove-discount]').forEach((b) => {
          b.disabled = isLoading;
        });
        this.spinner?.classList.toggle('hidden', !isLoading);
        if (isLoading && this.feedback) {
          this.feedback.textContent = '';
          this.feedback.classList.add('hidden');
        }
      }

      /**
       * Apply (or, with an empty code, remove) a discount.
       *
       * `/cart/update.js` takes a `discount` field and answers with `discount_codes`, where
       * each entry carries `applicable` — an authoritative yes/no straight from Shopify. It
       * also re-renders the sections in the same response.
       *
       * This replaces loading `/discount/CODE` in a hidden iframe and then diffing the
       * re-rendered HTML to guess the outcome: that resolved identically for a good code and
       * a rejected one, so success had to be inferred from whether any discount markup
       * happened to be present.
       */
      submitDiscount(code, submittedCode) {
        this.setLoading(true);

        const targets = this.renderTargets();
        const sections = targets
          ? targets.map((t) => t.section)
          : (this.dataset.sections || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);

        fetch(`${routes.cart_url}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            discount: code,
            sections,
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((cart) => {
            const wanted = submittedCode.trim().toLowerCase();
            const entry = (cart.discount_codes || []).find((d) => (d.code || '').toLowerCase() === wanted);
            // Removing succeeds when the code is gone; applying succeeds when it came back
            // marked applicable.
            const ok = code === '' ? !entry : Boolean(entry && entry.applicable);

            // Only a completed re-render replaces this element with a fresh, non-loading one.
            // If the response carried no sections, or rendering threw, the old element is
            // still on the page and would otherwise stay disabled with the spinner running
            // forever.
            let rerendered = false;
            try {
              if (cart.sections) rerendered = this.renderCart(cart.sections);
            } catch (renderError) {
              console.error('Cart failed to re-render after a discount change:', renderError.message);
            }
            if (!rerendered) this.setLoading(false);
            this.constructor.showResult(this.id, ok, submittedCode, code === '');
          })
          .catch((error) => {
            console.error('Error updating discount:', error.message);
            this.setLoading(false);
            this.constructor.showResult(this.id, false, submittedCode, code === '');
          });
      }

      /**
       * The cart-page sections to ask for and paint back, or null when this panel is inside
       * the drawer and the drawer re-renders itself.
       *
       * The section id cannot be hardcoded: for template sections it is generated per
       * template (`template--123__main-cart-items`) and only the container's `data-id`
       * knows it, which is exactly how cart.js resolves the same set. Containers that
       * aren't on this page are dropped rather than requested.
       */
      renderTargets() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && drawer.contains(this)) return null;

        return [
          { id: 'main-cart-items', selector: '.js-contents' },
          { id: 'main-cart-footer', selector: '.js-contents' },
          { id: 'cart-icon-bubble', selector: '.shopify-section' },
          { id: 'cart-live-region-text', selector: '.shopify-section' },
          // This theme also shows the cart total in the header. cart.js refreshes it on every
          // cart change, so a coupon applied from the cart page has to as well or the header
          // keeps the pre-discount figure until the next page load.
          { id: 'cart-total-label', selector: '.shopify-section' },
        ]
          .map((target) => {
            const container = document.getElementById(target.id);
            if (!container) return null;
            return { ...target, section: container.dataset.id || target.id };
          })
          .filter(Boolean);
      }

      /**
       * Paint the returned sections back into the page.
       *
       * The drawer replaces itself wholesale through its own renderContents(); the cart page
       * has no such element, so its sections are swapped the same way cart.js does it — by
       * `.js-contents` where the section defines one, else the container itself.
       *
       * Returns whether anything was actually replaced, because the caller uses that to
       * decide if it still owns a stuck spinner.
       */
      renderCart(sections) {
        const targets = this.renderTargets();
        if (targets === null) {
          const drawer = document.querySelector('cart-drawer');
          if (typeof drawer?.renderContents !== 'function') return false;
          drawer.renderContents({ sections });
          return true;
        }

        let replaced = false;
        targets.forEach(({ id, selector, section }) => {
          const html = sections[section];
          if (html == null) return;
          const source = new DOMParser().parseFromString(html, 'text/html');
          const incoming = source.querySelector(selector) || source.getElementById(id);
          if (!incoming) return;
          const destination = document.getElementById(id)?.querySelector(selector) || document.getElementById(id);
          if (!destination) return;
          destination.innerHTML = incoming.innerHTML;
          replaced = true;
        });
        return replaced;
      }

      /**
       * Runs after the cart has been re-rendered, so it works on the NEW nodes — `this` may
       * belong to an element that was just thrown away. The panel is found by the id it was
       * rendered with, which survives the swap and is unique per instance.
       */
      static showResult(panelId, ok, code, wasRemoval) {
        const form = panelId ? document.getElementById(panelId) : document.querySelector('cart-coupon-form');
        if (!form) return;
        // Keep the panel open so the outcome is visible where the shopper was looking.
        const panel = form.closest('.promo-code-wrap');
        panel?.classList.add('open');
        panel?.querySelector('.cart-coupon-main')?.setAttribute('aria-expanded', 'true');

        const input = form.querySelector('.promo-input.couponCode');
        const feedback = form.querySelector('[data-coupon-feedback]');

        if (ok) {
          // An applied code now renders as a chip above the field, so the field starts clean.
          if (input && !wasRemoval) input.value = '';
          // Writing the text in (rather than un-hiding fixed text) is what makes the live
          // region actually announce. Removal needs no confirmation — the chip disappearing
          // is the feedback.
          if (feedback && !wasRemoval) {
            feedback.textContent = feedback.dataset.appliedText || '';
            feedback.classList.remove('coupon_error', 'hidden');
            feedback.classList.add('coupon_success');
            input?.focus();
          } else if (feedback) {
            feedback.textContent = '';
            feedback.classList.add('hidden');
          }
          return;
        }

        if (input && !wasRemoval) input.value = code;
        // Re-rendering destroys the element that had focus, dropping the keyboard user back
        // to the top of the document. Returning focus to the coupon field keeps them where
        // they were, and puts them next to the message that was just announced.
        input?.focus();
        if (feedback) {
          feedback.textContent = feedback.dataset.errorText || '';
          feedback.classList.remove('coupon_success', 'hidden');
          feedback.classList.add('coupon_error');
        }
      }
    }
  );
}
